import React, { useEffect, useRef, useState } from "react";
import "./media.css";

/**
 * Natural video sizing for the feed.
 * - Landscape stays natural (contain)
 * - Portrait fills width + crops evenly (cover) via CSS .is-portrait
 * - Auto-pause when off-screen
 * - Only one video plays at a time
 */
export default function PostVideo({
  src,
  poster,
  autoPlay = false,
  controls = true,
  onOpen,
}) {
  const videoRef = useRef(null);
  const [ratio, setRatio] = useState(null);
  const [isPortrait, setIsPortrait] = useState(false);

  // Size/orientation
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => {
      const w = v.videoWidth || 0;
      const h = v.videoHeight || 0;
      if (w > 0 && h > 0) {
        setRatio(`${w} / ${h}`);
        setIsPortrait(h > w);
      }
    };
    v.addEventListener("loadedmetadata", onMeta, { once: true });
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, [src]);

  // Pause when scrolled out of view
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !v.paused) {
          try { v.pause(); } catch {}
        }
      },
      { threshold: 0.3 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  // Only one video playing at a time
  const handlePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    const prev = window.__bfNowPlaying;
    if (prev && prev !== v) {
      try { prev.pause(); } catch {}
    }
    window.__bfNowPlaying = v;
  };
  const handlePause = () => {
    const v = videoRef.current;
    if (window.__bfNowPlaying === v) {
      window.__bfNowPlaying = null;
    }
  };

  // Pause if tab/app loses visibility
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        try { videoRef.current?.pause(); } catch {}
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const wrapClass = `bf-video-wrap ${isPortrait ? "is-portrait" : "is-landscape"}`;

  // Helper so tap zones pause before opening fullscreen
  const openAndPause = () => {
    try { videoRef.current?.pause(); } catch {}
    onOpen?.();
  };

  return (
    <div className={wrapClass} style={ratio ? { aspectRatio: ratio } : undefined}>
      <video
        ref={videoRef}
        className="bf-post-video"
        playsInline
        controls={controls}
        autoPlay={autoPlay}
        preload="metadata"
        poster={poster || undefined}
        onPlay={handlePlay}
        onPause={handlePause}
        onLoadedMetadata={(e) => {
          try {
            if (e.currentTarget.currentTime === 0) {
              e.currentTarget.currentTime = 0.01;
              e.currentTarget.pause();
            }
          } catch {}
        }}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      >
        <source src={src} type="video/mp4" />
      </video>

      {onOpen && (
        <>
          <button className="video-hit zone-top" onClick={openAndPause} />
          <button className="video-hit zone-bottom" onClick={openAndPause} />
          <button className="video-hit zone-left" onClick={openAndPause} />
          <button className="video-hit zone-right" onClick={openAndPause} />
        </>
      )}
    </div>
  );
}
